import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  requestGetBusinessServiceCategory,
  requestListServiceByCategoryName,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { useNavigate, useParams } from "react-router-dom";
import { useTitle } from "../../Hooks/useTitle";
import { Alert } from "../../Components/Alert";
import { classNames } from "../../Utils";
import { CheckIcon } from "@heroicons/react/outline";
import { NumericFormat } from "react-number-format";
import { EmptyList } from "../../Components/EmptyList";
import ConsultantBadge from "../../Components/ConsultantBadge";

const ServiceCard = ({ businessService, navigate, t }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionLength = businessService.description?.length || 0;
  const shouldShowReadMore = descriptionLength > 150;

  return (
    <div
      className={classNames(
        "group relative flex flex-col overflow-hidden rounded-xl border transition-all duration-300 hover:shadow-lg",
        businessService.status === "UNAVAILABLE"
          ? "bg-gray-50 border-gray-200"
          : "bg-white border-gray-100 hover:border-blue-200"
      )}
    >
      {/* Header Section */}
      <div className="p-6 pb-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
          {businessService.name}
        </h3>

        {/* Price */}
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("startingFrom")}
            </span>
            {businessService.basePrice &&
              businessService.basePrice > businessService.price && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                  -
                  {Math.round(
                    ((businessService.basePrice - businessService.price) /
                      businessService.basePrice) *
                      100
                  )}
                  %
                </span>
              )}
          </div>
          <div className="flex items-baseline gap-2">
            <NumericFormat
              displayType={"text"}
              value={businessService.price}
              thousandSeparator={"."}
              decimalSeparator={","}
              prefix={"Rp"}
              className="text-2xl font-bold text-gray-900"
            />
            {businessService.basePrice &&
              businessService.basePrice > businessService.price && (
                <NumericFormat
                  displayType={"text"}
                  value={businessService.basePrice}
                  thousandSeparator={"."}
                  decimalSeparator={","}
                  prefix={"Rp"}
                  className="text-sm font-medium text-gray-400 line-through"
                />
              )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100"></div>

      {/* Content Section */}
      <div className="flex-1 p-6 space-y-4 min-h-[280px]">
        <div>
          <p
            className={classNames(
              "text-sm text-gray-600 leading-relaxed",
              !isExpanded && shouldShowReadMore && "line-clamp-3"
            )}
          >
            {businessService.description}
          </p>
          {shouldShowReadMore && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
            >
              {isExpanded ? t("Show less") : t("Read more")}
            </button>
          )}
        </div>

        {/* Consultant info */}
        <ConsultantBadge consultant={businessService.consultant} />

        {/* Benefits */}
        <ul className="space-y-2.5 max-h-[140px] overflow-y-auto">
          {businessService.benefits
            .filter((benefit) => benefit && benefit.trim() !== "")
            .map((benefit) => (
              <li key={benefit} className="flex items-start gap-2">
                <CheckIcon
                  className="flex-shrink-0 w-4 h-4 text-blue-500 mt-0.5"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-600">{t(benefit)}</span>
              </li>
            ))}
        </ul>
      </div>

      {/* Button Section */}
      <div className="p-6 pt-4">
        <button
          onClick={() =>
            navigate(`/business-service/form/${businessService.id}`)
          }
          disabled={businessService.status === "UNAVAILABLE"}
          className={classNames(
            "w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200",
            businessService.status === "UNAVAILABLE"
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow"
          )}
        >
          {t(
            businessService.status === "UNAVAILABLE"
              ? "Not Available"
              : "Order"
          )}
        </button>
      </div>
    </div>
  );
};

const BusinessLicenseDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useBusinessServiceDispatch();
  const {
    businessServiceCategory,
    serviceCategoryLoading,
    serviceCategoryError,
    businessServices,
    businessServicesLoading,
    businessServicesError,
  } = useBusinessServiceState();

  useTitle(
    t(
      businessServiceCategory?.name
        ? `Layanan ${businessServiceCategory?.name}`
        : `Layanan Usaha`
    )
  );

  useEffect(() => {
    requestGetBusinessServiceCategory(dispatch, { id });
  }, [dispatch, id]);

  useEffect(() => {
    if (businessServiceCategory?.id) {
      requestListServiceByCategoryName(dispatch, {
        categoryID: businessServiceCategory.id,
      });
    }
  }, [businessServiceCategory?.id, dispatch]);

  const BusinessServiceSkeleton = ({ length }) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {Array.from({ length }).map((_, index) => (
          <div
            key={index}
            className="relative flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white animate-pulse"
          >
            {/* Header Section */}
            <div className="p-6 pb-4">
              <div className="h-5 w-3/4 bg-gray-300 rounded mb-2"></div>
              <div className="h-7 w-1/2 bg-gray-300 rounded"></div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100"></div>

            {/* Content Section */}
            <div className="flex-1 p-6 space-y-4">
              {/* Description */}
              <div className="space-y-2">
                <div className="h-3 w-full bg-gray-300 rounded"></div>
                <div className="h-3 w-5/6 bg-gray-300 rounded"></div>
              </div>

              {/* Consultant Card */}
              <div className="px-3 py-2 bg-gray-100 rounded-lg space-y-2">
                <div className="h-3 w-1/3 bg-gray-300 rounded"></div>
                <div className="h-4 w-2/3 bg-gray-300 rounded"></div>
              </div>

              {/* Benefits */}
              <div className="space-y-2.5">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-gray-300 rounded flex-shrink-0 mt-0.5"></div>
                  <div className="h-3 w-full bg-gray-300 rounded"></div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-gray-300 rounded flex-shrink-0 mt-0.5"></div>
                  <div className="h-3 w-5/6 bg-gray-300 rounded"></div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-gray-300 rounded flex-shrink-0 mt-0.5"></div>
                  <div className="h-3 w-4/5 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>

            {/* Button Section */}
            <div className="p-6 pt-4">
              <div className="h-10 w-full bg-gray-300 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl lg:max-w-7xl">
      <div className="max-w-7xl mx-auto py-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <h1 className="text-xl font-bold text-gray-900">
              {serviceCategoryLoading || businessServicesLoading ? (
                <div className="h-6 bg-gray-300 rounded w-3/4 animate-pulse"></div>
              ) : businessServicesError || serviceCategoryError ? (
                t("Layanan Perizinan Usaha")
              ) : businessServiceCategory?.name ? (
                t(`Layanan ${businessServiceCategory.name}`)
              ) : (
                t("Layanan Perizinan Usaha")
              )}
            </h1>
          </div>
        </dl>
      </div>
      {serviceCategoryLoading || businessServicesLoading ? (
        <BusinessServiceSkeleton length={3} />
      ) : businessServicesError || serviceCategoryError ? (
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={
              businessServicesError?.message || serviceCategoryError?.message
            }
            errors={
              businessServicesError?.errors || serviceCategoryError?.errors
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {businessServices.length > 0 ? (
            businessServices.map((businessService) => (
              <ServiceCard
                key={businessService.id}
                businessService={businessService}
                navigate={navigate}
                t={t}
              />
            ))
          ) : (
            <div className="bg-white col-span-12 rounded-lg p-4">
              <EmptyList title="noServicesAvailable" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessLicenseDetail;
